import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Calendar, ArrowRight, ArrowLeft, Tag, Phone, List, ChevronDown, Link2, Check, Clock } from "lucide-react";
import SEO from "../components/SEO";
import FloatingBlogCta from "../components/FloatingBlogCta";
import BlogInlineForm from "../components/BlogInlineForm";
import { api } from "../lib/api";
import { PHONE, PHONE_TEL } from "../data/content";

function fmtDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

function readingTime(content) {
  if (!content) return 1;
  // Strip simple markdown syntax for a more honest word count
  const cleaned = content
    .replace(/[#>*_`-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const words = cleaned ? cleaned.split(" ").length : 0;
  return Math.max(1, Math.round(words / 220));
}

function optimizedImageUrl(url, width) {
  if (!url) return url;
  // Only rewrite Unsplash URLs — local/static images pass through unchanged.
  if (!/images\.unsplash\.com/.test(url)) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("fm", "webp");
    u.searchParams.set("auto", "compress");
    u.searchParams.set("q", "78");
    if (width) u.searchParams.set("w", String(width));
    return u.toString();
  } catch {
    return url;
  }
}

function CoverImage({ src, alt, width, eager = false, overlay = false, className = "" }) {
  if (!src) return null;
  const optimized = optimizedImageUrl(src, width);
  return (
    <div className={`relative aspect-video overflow-hidden ${className}`}>
      <img
        src={optimized}
        alt={alt || ""}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
        fetchPriority={eager ? "high" : "auto"}
        className="w-full h-full object-cover"
      />
      {overlay && (
        <div
          aria-hidden="true"
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "linear-gradient(rgba(5,5,5,0.3), rgba(5,5,5,0.7))",
          }}
        />
      )}
    </div>
  );
}

function slugifyHeading(text) {
  return (text || "")
    .toLowerCase()
    .replace(/[^\w\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 80);
}

function H2Anchor({ id, children }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = async (e) => {
    e.preventDefault();
    const href = `${window.location.origin}${window.location.pathname}#${id}`;
    try {
      await navigator.clipboard.writeText(href);
    } catch {
      // Fallback for very old browsers / insecure contexts
      const ta = document.createElement("textarea");
      ta.value = href;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* noop */ }
      document.body.removeChild(ta);
    }
    history.replaceState(null, "", `#${id}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <h2 id={id} className="scroll-mt-24 group flex items-center gap-3">
      <span>{children}</span>
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy link to this section"
        data-testid={`h2-copy-${id}`}
        className="inline-flex items-center justify-center w-7 h-7 border border-white/10 text-zinc-500 hover:text-white hover:border-white/30 transition opacity-0 group-hover:opacity-100 focus:opacity-100"
      >
        {copied ? (
          <Check size={13} style={{ color: "var(--te-primary)" }} />
        ) : (
          <Link2 size={13} />
        )}
      </button>
    </h2>
  );
}

function renderMarkdown(md) {
  // Tiny markdown subset: ## h2, ### h3, **bold**, paragraphs, ordered/unordered lists
  // Also extracts H2 headings for table of contents.
  const lines = (md || "").split("\n");
  const out = [];
  const headings = [];
  const slugCounts = {};
  let listType = null;
  let listItems = [];
  let quoteLines = [];
  let tableRows = [];
  const flushList = () => {
    if (listItems.length) {
      const Tag = listType === "ol" ? "ol" : "ul";
      out.push(
        <Tag key={`list-${out.length}`}>
          {listItems.map((it, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: inline(it) }} />
          ))}
        </Tag>
      );
      listItems = [];
      listType = null;
    }
  };
  const flushQuote = () => {
    if (quoteLines.length) {
      out.push(
        <blockquote key={`quote-${out.length}`}>
          {quoteLines.map((q, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: inline(q) }} />
          ))}
        </blockquote>
      );
      quoteLines = [];
    }
  };
  const flushTable = () => {
    if (tableRows.length) {
      const [head, ...body] = tableRows;
      out.push(
        <div key={`table-${out.length}`} className="te-prose-table-wrap">
          <table>
            <thead>
              <tr>
                {head.map((c, i) => (
                  <th key={i} dangerouslySetInnerHTML={{ __html: inline(c) }} />
                ))}
              </tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri}>
                  {row.map((c, ci) => (
                    <td key={ci} dangerouslySetInnerHTML={{ __html: inline(c) }} />
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
      tableRows = [];
    }
  };
  const flushAll = () => {
    flushList();
    flushQuote();
    flushTable();
  };
  const escapeAttr = (s) =>
    String(s).replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const inline = (s) =>
    s.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
     .replace(/`(.+?)`/g, "<code>$1</code>")
     .replace(/\[([^\]]+)\]\(([^)]+)\)/g, (_, text, url) => {
       const safe = escapeAttr(url);
       const external = /^https?:\/\//i.test(url);
       const attrs = external
         ? ` target="_blank" rel="noopener noreferrer"`
         : "";
       return `<a href="${safe}" class="te-prose-link"${attrs}>${text}</a>`;
     });
  lines.forEach((rawLine, idx) => {
    const line = rawLine.trimEnd();
    if (!line.trim()) {
      flushAll();
      return;
    }
    if (line.startsWith("### ")) {
      flushAll();
      out.push(<h3 key={idx}>{line.slice(4)}</h3>);
    } else if (line.startsWith("## ")) {
      flushAll();
      const text = line.slice(3);
      let slug = slugifyHeading(text) || `section-${idx}`;
      slugCounts[slug] = (slugCounts[slug] || 0) + 1;
      if (slugCounts[slug] > 1) slug = `${slug}-${slugCounts[slug]}`;
      headings.push({ slug, text });
      out.push(
        <H2Anchor key={idx} id={slug}>
          {text}
        </H2Anchor>
      );
    } else if (/^\d+\.\s/.test(line)) {
      if (listType !== "ol") flushList();
      flushQuote();
      flushTable();
      listType = "ol";
      listItems.push(line.replace(/^\d+\.\s/, ""));
    } else if (line.startsWith("- ")) {
      if (listType !== "ul") flushList();
      flushQuote();
      flushTable();
      listType = "ul";
      listItems.push(line.slice(2));
    } else if (line.startsWith("> ")) {
      flushList();
      flushTable();
      quoteLines.push(line.slice(2));
    } else if (line.trim().startsWith("|")) {
      flushList();
      flushQuote();
      if (/^\|?\s*:?-{2,}/.test(line.replace(/\|/g, "").trim()) || /^\|[\s|:-]+\|$/.test(line.trim())) {
        return; // separator row
      }
      const cells = line.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());
      tableRows.push(cells);
    } else if (/^\[\[(compliance-form|booking-form)\]\]\s*$/.test(line.trim())) {
      flushAll();
      const variant = line.trim() === "[[compliance-form]]" ? "compliance" : "booking";
      out.push(<BlogInlineForm key={idx} variant={variant} />);
    } else {
      flushAll();
      out.push(<p key={idx} dangerouslySetInnerHTML={{ __html: inline(line) }} />);
    }
  });
  flushAll();
  return { nodes: out, headings };
}

function useActiveHeading(slugs) {
  const [active, setActive] = useState(slugs[0] || null);
  useEffect(() => {
    if (!slugs.length) return;
    const compute = () => {
      const offset = 120; // pixels below top of viewport
      let current = slugs[0];
      for (const s of slugs) {
        const el = document.getElementById(s);
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - offset <= 0) {
          current = s;
        } else {
          break;
        }
      }
      setActive(current);
    };
    compute();
    window.addEventListener("scroll", compute, { passive: true });
    window.addEventListener("resize", compute);
    return () => {
      window.removeEventListener("scroll", compute);
      window.removeEventListener("resize", compute);
    };
  }, [slugs.join("|")]); // eslint-disable-line react-hooks/exhaustive-deps
  return active;
}

function TableOfContents({ headings, variant = "sidebar" }) {
  const [open, setOpen] = useState(false);
  const slugs = useMemo(() => headings.map((h) => h.slug), [headings]);
  const active = useActiveHeading(slugs);

  if (headings.length < 2) return null;

  const handleClick = (e, slug) => {
    e.preventDefault();
    const el = document.getElementById(slug);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Update URL hash without jumping
      history.replaceState(null, "", `#${slug}`);
    }
    setOpen(false);
  };

  const list = (
    <ul className="space-y-1.5" data-testid="toc-list">
      {headings.map((h) => {
        const isActive = h.slug === active;
        return (
          <li key={h.slug}>
            <a
              href={`#${h.slug}`}
              onClick={(e) => handleClick(e, h.slug)}
              data-testid={`toc-link-${h.slug}`}
              className={`block text-sm leading-snug border-l-2 pl-3 py-1 transition-colors ${
                isActive
                  ? "text-white border-[var(--te-primary)]"
                  : "text-zinc-500 hover:text-zinc-200 border-white/10 hover:border-white/30"
              }`}
            >
              {h.text}
            </a>
          </li>
        );
      })}
    </ul>
  );

  if (variant === "mobile") {
    return (
      <div className="xl:hidden mb-10 border border-white/10" data-testid="toc-mobile">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          data-testid="toc-toggle"
          className="w-full flex items-center justify-between px-4 py-3 hover:bg-white/5 transition-colors"
        >
          <span className="te-overline text-[10px] flex items-center gap-2">
            <List size={12} /> In this article ({headings.length})
          </span>
          <ChevronDown
            size={16}
            className={`text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {open && (
          <div className="px-4 pb-4 pt-1 border-t border-white/10">{list}</div>
        )}
      </div>
    );
  }

  return (
    <aside
      className="hidden xl:block"
      aria-label="Table of contents"
      data-testid="toc-sidebar"
    >
      <div className="sticky top-24">
        <div className="te-overline text-[10px] mb-4 flex items-center gap-2">
          <List size={12} /> In this article
        </div>
        {list}
      </div>
    </aside>
  );
}

export function Blog() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).catch(() => setPosts([])).finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEO
        title="Adelaide Tech & Cyber Security Blog | Tech Emergency"
        description="Practical guides on scam awareness, WiFi fixes, cyber security and computer maintenance — written by Adelaide's mobile IT team."
        path="/blog"
      />
      <section className="pt-20 pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="te-overline mb-4">/ Field notes</div>
          <h1 className="text-5xl sm:text-6xl font-bold tracking-tighter leading-none mb-6">
            Tech, scams &<br />actual fixes.
          </h1>
          <p className="text-zinc-400 max-w-2xl text-lg leading-relaxed">
            Plain-English guides written by the techs who actually do the on-site work across Adelaide.
          </p>
        </div>
      </section>

      <section className="pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="te-overline">Loading…</div>
          ) : posts.length === 0 ? (
            <div className="te-overline">No posts yet.</div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {posts.map((p) => (
                <Link key={p.id} to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="te-card overflow-hidden group">
                  {p.cover_image && (
                    <CoverImage
                      src={p.cover_image}
                      alt={p.title}
                      width={800}
                      overlay
                    />
                  )}
                  <div className="p-6">
                    <div className="te-overline text-[10px] mb-3 flex items-center gap-3 flex-wrap">
                      <span className="inline-flex items-center gap-1.5">
                        <Calendar size={12} /> {fmtDate(p.created_at)}
                      </span>
                      <span className="inline-flex items-center gap-1.5">
                        <Clock size={12} /> {readingTime(p.content)} min read
                      </span>
                    </div>
                    <h2 className="text-xl font-semibold mb-2 tracking-tight group-hover:text-[var(--te-primary)] transition-colors">
                      {p.title}
                    </h2>
                    <p className="text-sm text-zinc-400 leading-relaxed mb-4">{p.excerpt}</p>
                    <div className="text-sm font-medium flex items-center gap-2" style={{ color: "var(--te-primary)" }}>
                      Read <ArrowRight size={14} />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [state, setState] = useState("loading");

  useEffect(() => {
    setState("loading");
    setRelated([]);
    api.get(`/blog/${slug}`).then((r) => { setPost(r.data); setState("ok"); }).catch(() => setState("err"));
    api.get(`/blog/${slug}/related`).then((r) => setRelated(r.data || [])).catch(() => setRelated([]));
  }, [slug]);

  // Intercept clicks on internal markdown links so they use react-router instead of a full page reload.
  useEffect(() => {
    const prose = document.querySelector(".te-prose");
    if (!prose) return;
    const onClick = (e) => {
      const a = e.target.closest("a.te-prose-link");
      if (!a) return;
      const href = a.getAttribute("href") || "";
      if (href.startsWith("/")) {
        e.preventDefault();
        navigate(href);
      }
    };
    prose.addEventListener("click", onClick);
    return () => prose.removeEventListener("click", onClick);
  }, [post, navigate]);

  const { nodes, headings } = useMemo(
    () => (post ? renderMarkdown(post.content) : { nodes: [], headings: [] }),
    [post]
  );

  if (state === "loading") {
    return <section className="pt-24 pb-24 max-w-3xl mx-auto px-4"><div className="te-overline">Loading…</div></section>;
  }
  if (state === "err" || !post) {
    return (
      <>
        <SEO
          title="Post not found · Tech Emergency Blog"
          description="That blog post couldn't be found. Browse all Tech Emergency Adelaide IT articles."
          path="/blog"
          noIndex
        />
        <section className="pt-24 pb-24 max-w-3xl mx-auto px-4 text-center">
          <div className="te-overline mb-3">/ 404</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter mb-3">Post not found.</h1>
          <p className="text-zinc-400 mb-6">That article doesn&apos;t exist or has been moved.</p>
          <Link to="/blog" className="te-btn te-btn-primary mt-4 inline-flex">
            <ArrowLeft size={16} /> Back to all posts
          </Link>
        </section>
      </>
    );
  }

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    image: post.cover_image,
    datePublished: post.created_at,
    dateModified: post.updated_at,
    author: { "@type": "Organization", name: "Tech Emergency" },
    publisher: { "@type": "Organization", name: "Tech Emergency" },
  };

  // If this post has an embedded compliance form, expose it as a Service +
  // ReserveAction so Google understands the page also offers a bookable action.
  const SITE_BASE = "https://www.techemergency.com.au";
  const fullPath = `${SITE_BASE}/blog/${post.slug}`;
  const hasComplianceForm = /\[\[compliance-form\]\]/.test(post.content || "");
  const serviceJsonLd = hasComplianceForm
    ? {
        "@context": "https://schema.org",
        "@type": "Service",
        "@id": `${fullPath}#service`,
        name: "Free 30-minute on-site cyber compliance check",
        description:
          "No-obligation, on-site cyber-security and Essential Eight compliance assessment for Adelaide small businesses. Honest priorities, clear baseline, no scare tactics.",
        serviceType: "Cyber security compliance assessment",
        provider: { "@id": "https://www.techemergency.com.au/#business" },
        areaServed: {
          "@type": "City",
          name: "Adelaide",
          address: {
            "@type": "PostalAddress",
            addressRegion: "SA",
            addressCountry: "AU",
          },
        },
        offers: {
          "@type": "Offer",
          price: "0",
          priceCurrency: "AUD",
          availability: "https://schema.org/InStock",
          url: fullPath,
        },
        potentialAction: {
          "@type": "ReserveAction",
          name: "Request a free compliance check",
          target: {
            "@type": "EntryPoint",
            urlTemplate: fullPath,
            inLanguage: "en-AU",
            actionPlatform: [
              "https://schema.org/DesktopWebPlatform",
              "https://schema.org/MobileWebPlatform",
            ],
          },
          result: {
            "@type": "Reservation",
            name: "Cyber compliance check booking",
          },
        },
      }
    : null;

  const jsonLdBlocks = [articleJsonLd, serviceJsonLd].filter(Boolean);

  return (
    <>
      <SEO
        title={`${post.title} | Tech Emergency Blog`}
        description={post.excerpt}
        path={`/blog/${post.slug}`}
        type="article"
        image={post.cover_image}
        jsonLd={jsonLdBlocks}
      />
      <article className="pt-16 pb-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_240px] xl:gap-12">
            <div className="max-w-3xl mx-auto xl:mx-0 w-full">
              <Link to="/blog" className="te-overline inline-flex items-center gap-2 hover:text-white mb-6">
                <ArrowLeft size={14} /> All posts
              </Link>
              <div className="te-overline text-[10px] mb-3 flex items-center gap-3 flex-wrap">
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} /> {fmtDate(post.created_at)}
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <Clock size={12} /> {readingTime(post.content)} min read
                </span>
              </div>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tighter leading-tight mb-6">{post.title}</h1>
              {post.tags?.length > 0 && (
                <div className="flex gap-2 flex-wrap mb-8">
                  {post.tags.map((t) => (
                    <span key={t} className="te-overline text-[10px] px-2 py-1 border border-white/10">
                      <Tag size={10} className="inline mr-1" /> {t}
                    </span>
                  ))}
                </div>
              )}
              {post.cover_image && (
                <div className="mb-10 border border-white/10">
                  <CoverImage
                    src={post.cover_image}
                    alt={post.title}
                    width={1280}
                    eager
                  />
                </div>
              )}

              <TableOfContents headings={headings} variant="mobile" />

              <div className="te-prose">{nodes}</div>

              {related.length > 0 && (
                <div className="mt-16 pt-10 border-t border-white/10" data-testid="related-posts">
                  <div className="te-overline mb-3">/ Recommended for you</div>
                  <h3 className="text-2xl sm:text-3xl font-bold tracking-tight mb-8">
                    More from the field.
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {related.map((p) => (
                      <Link
                        key={p.id}
                        to={`/blog/${p.slug}`}
                        data-testid={`related-card-${p.slug}`}
                        className="te-card overflow-hidden group"
                      >
                        {p.cover_image && (
                          <CoverImage
                            src={p.cover_image}
                            alt={p.title}
                            width={500}
                            overlay
                          />
                        )}
                        <div className="p-5">
                          <div className="te-overline text-[10px] mb-2 flex items-center gap-2">
                            <Calendar size={11} /> {fmtDate(p.created_at)}
                          </div>
                          <h4 className="text-base font-semibold tracking-tight mb-2 leading-snug group-hover:text-[var(--te-primary)] transition-colors">
                            {p.title}
                          </h4>
                          <div className="text-xs font-medium flex items-center gap-1.5" style={{ color: "var(--te-primary)" }}>
                            Read <ArrowRight size={12} />
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              <div className="mt-16 te-card p-6 md:p-8">
                <div className="te-overline mb-3">/ Need help with this?</div>
                <h3 className="text-2xl font-bold tracking-tight mb-3">
                  We do this on-site across Adelaide.
                </h3>
                <p className="text-sm text-zinc-400 mb-5">Free over-the-phone triage. Police-checked technician.</p>
                <a href={PHONE_TEL} className="te-btn te-btn-primary" data-testid="blog-cta-call">
                  <Phone size={16} /> {PHONE}
                </a>
              </div>
            </div>

            <TableOfContents headings={headings} variant="sidebar" />
          </div>
        </div>
      </article>

      <FloatingBlogCta slug={post.slug} />

      <style>{`
        .te-prose ul { list-style: disc; }
        .te-prose ol { list-style: decimal; }
        .te-prose .te-prose-link {
          color: var(--te-primary);
          border-bottom: 1px solid rgba(255,255,255,0.15);
          transition: color 0.15s, border-color 0.15s;
        }
        .te-prose .te-prose-link:hover {
          color: #fff;
          border-bottom-color: var(--te-primary);
        }
      `}</style>
    </>
  );
}
